import React, { useEffect, useState, useRef } from 'react';
import './CombinedReport.page.css';
import { useParams, useNavigate } from 'react-router-dom';
import { UserUtils } from '../../utils/UserUtils';
import UserTokenData from '../../types/UserTokenData';
import { LoginState } from '../../types/LoginState';
import MainTopBar from '../../ui/panels/TopBars/MainTopBar/MainTopBar';
import CICPLetterPage from '../CICPLetter/CICPLetter.page';
import QuestionnaireReadOnly from '../../ui/components/QuestionnaireReadOnly/QuestionnaireReadOnly';
import html2pdf from 'html2pdf.js';
// @ts-ignore - html-docx-js doesn't have TypeScript definitions
import htmlDocx from 'html-docx-js/dist/html-docx';

interface PatientDetailObject {
  status: string;
  email: string;
  firstName: string;
  entityId: string;
  lastName: string;
  patientAssignee: {
    Lawyer: Array<{
      firstName: string;
      lastName: string;
      email: string;
      userEntityAccessId: string;
    }>;
    Physician: Array<{
      firstName: string;
      lastName: string;
      userEntityAccessId: string;
    }>;
  };
}

interface CitationItem {
  citationId: number;
  citation: string;
  rationale: string;
  dateAdded: string | null;
  dateUpdated: string | null;
  addedBy: string | null;
  updatedBy: string | null;
}

interface SymptomCitations {
  symptomId: number;
  symptom: string;
  citations: CitationItem[];
}

function CombinedReportPage() {
  const [token, setToken] = useState<string>(
    localStorage.getItem('token') || ''
  );
  const { userId } = useParams<{ userId: string }>();
  const nav = useNavigate();
  const printRef = useRef<HTMLDivElement>(null);

  const [userDetails, setUserDetails] = useState<UserTokenData>({
    userName: '',
    id: '',
    email: '',
    role: '',
  });

  const [patientDetails, setPatientDetails] = useState<PatientDetailObject>({
    status: '',
    email: '',
    firstName: '',
    entityId: '',
    lastName: '',
    patientAssignee: {
      Lawyer: [],
      Physician: [],
    },
  });

  const [loginState, setLoginState] = useState<LoginState>({
    isLoggedIn: false,
    isAdmin: false,
  });

  const [isLoading, setIsLoading] = useState(true);
  const [symptomCitations, setSymptomCitations] = useState<SymptomCitations[]>(
    []
  );

  // Convert text to title case
  const toTitleCase = (str: string): string => {
    if (!str) return str;
    return str
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  useEffect(() => {
    if (token == '') {
      nav('/signin');
    }
    const userDetails = UserUtils.decodeUserToken(token);
    if (!userDetails) {
      nav('/signin');
    } else {
      setUserDetails(userDetails);
      console.log('userDetails', userDetails);
    }
  }, [token]);

  const validateUserLogin = async () => {
    if (token) {
      const url = '/validate';
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        setLoginState({ isLoggedIn: false, isAdmin: false });
        nav('/');
        return;
      }
      const json = await response.json();

      if (json.valid && json.valid === true) {
        setLoginState({ isLoggedIn: true, isAdmin: true });
      } else {
        setLoginState({ isLoggedIn: true, isAdmin: false });
      }
    }
  };

  const getUserDetails = async () => {
    console.log('userId', userId);
    const url = `/admin/caseDetails/${userId}?detailed=true`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    const data = await response.json();
    console.log('patientDetails', data);
    setPatientDetails(data);
    setIsLoading(false);
    try {
      if (data?.entityId) {
        const citationsRes = await fetch(
          `/citations/by-entity/${data.entityId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (citationsRes.ok) {
          const cdata: SymptomCitations[] = await citationsRes.json();
          setSymptomCitations(cdata);
        }
      }
    } catch (e) {
      console.error('Failed to load citations for combined report', e);
    }
  };

  useEffect(() => {
    if (userDetails.id) {
      validateUserLogin();
      getUserDetails();
    }
  }, [userDetails.id]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    if (!printRef.current) return;

    // Clone the element to avoid modifying the original
    const clone = printRef.current.cloneNode(true) as HTMLElement;
    
    // Ensure symptom titles are in title case for PDF
    const symptomTitles = clone.querySelectorAll('.citation-symptom-title');
    symptomTitles.forEach((title) => {
      if (title.textContent) {
        title.textContent = toTitleCase(title.textContent);
      }
    });

    const element = clone;
    const patientName = `${patientDetails.firstName}_${patientDetails.lastName}`;
    const date = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format

    const options = {
      margin: [0.5, 0.5, 0.5, 0.5] as [number, number, number, number], // top, right, bottom, left in inches
      filename: `Patient_Report_${patientName}_${date}.pdf`,
      image: { type: 'jpeg' as const, quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        letterRendering: true,
        scrollY: 0,
        scrollX: 0,
      },
      jsPDF: {
        unit: 'in' as const,
        format: 'letter' as const,
        orientation: 'portrait' as const,
      },
      pagebreak: {
        mode: ['avoid-all', 'css', 'legacy'],
        before: '.page-break',
      },
    };

    try {
      await html2pdf().set(options).from(element).save();
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('There was an error generating the PDF. Please try again.');
    }
  };

  const handleDownloadWord = () => {
    if (!printRef.current) return;

    const patientName = `${patientDetails.firstName}_${patientDetails.lastName}`;
    const date = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    const filename = `Patient_Report_${patientName}_${date}.docx`;

    try {
      // Clone the element to avoid modifying the original
      const clone = printRef.current.cloneNode(true) as HTMLElement;
      
      // Remove no-print elements before generating Word document
      const noPrintElements = clone.querySelectorAll('.no-print');
      noPrintElements.forEach((el) => el.remove());

      // Ensure symptom titles are in title case for DOCX
      const symptomTitles = clone.querySelectorAll('.citation-symptom-title');
      symptomTitles.forEach((title) => {
        if (title.textContent) {
          title.textContent = toTitleCase(title.textContent);
        }
      });

      // Get HTML content and modify it to add Word-compatible page breaks
      // ONLY for CitationSection and TestFormSection - not for CICPLetterSection
      let htmlContent = clone.innerHTML;
      
      // Create page break elements that html-docx-js and Word will recognize
      // Using paragraph with Microsoft Office specific properties for better compatibility
      const pageBreakElement = '<p style="page-break-before: always; break-before: page; mso-page-break-before: always; margin: 0; padding: 0; height: 0; line-height: 0; font-size: 0;">&nbsp;</p>';
      
      // Only insert page break before CitationSection (Medical Citations & Research)
      // Make sure we're matching the exact class combination
      htmlContent = htmlContent.replace(
        /(<div[^>]*class="[^"]*ReportSection[^"]*CitationSection[^"]*"[^>]*>)/gi,
        pageBreakElement + '$1'
      );
      
      // Only insert page break before TestFormSection (Patient Questionnaire)
      htmlContent = htmlContent.replace(
        /(<div[^>]*class="[^"]*ReportSection[^"]*TestFormSection[^"]*"[^>]*>)/gi,
        pageBreakElement + '$1'
      );

      // Add comprehensive styling for Word document that matches the CSS
      const styledHtml = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8">
            <style>
              body { 
                font-family: 'Times New Roman', Times, serif; 
                font-size: 12pt;
                line-height: 1.5; 
                padding: 20px; 
                color: #000;
              }
              h2 { 
                font-family: 'Times New Roman', Times, serif;
                font-size: 16pt; 
                font-weight: 700; 
                color: #000; 
                margin-top: 20px; 
                margin-bottom: 15px; 
                padding-bottom: 0;
              }
              h5 { 
                font-family: 'Times New Roman', Times, serif;
                font-size: 14pt; 
                font-weight: 700; 
                color: #000; 
                margin-top: 20px; 
                margin-bottom: 12px; 
                padding: 0;
              }
              h4 { 
                font-family: 'Times New Roman', Times, serif;
                font-size: 12pt; 
                font-weight: 600; 
                color: #000; 
                margin-top: 15px; 
                margin-bottom: 8px; 
                line-height: 1.4;
              }
              p { 
                font-family: 'Times New Roman', Times, serif;
                font-size: 12pt; 
                color: #000; 
                margin: 8px 0; 
                text-align: justify;
                line-height: 1.8;
              }
              .SectionHeader { 
                padding-bottom: 10px; 
                margin-bottom: 20px; 
              }
              .ReportSection { 
                margin-bottom: 30px; 
                page-break-inside: avoid; 
              }
              /* Page break paragraph styling for Word compatibility - only for inserted page breaks */
              p[style*="page-break-before"] {
                page-break-before: always !important;
                break-before: page !important;
                mso-page-break-before: always !important;
                margin: 0 !important;
                padding: 0 !important;
                height: 0 !important;
                line-height: 0 !important;
                font-size: 0 !important;
              }
              /* DO NOT add page-break-before to all sections - only the inserted paragraphs will have it */
              .citation-symptom-group {
                margin-bottom: 25px;
                padding-bottom: 15px;
              }
              .citation-study-item {
                margin-bottom: 15px;
                margin-left: 0;
                padding-left: 0;
                padding-top: 8px;
                padding-bottom: 8px;
              }
              .no-print { display: none !important; }
            </style>
          </head>
          <body>
            ${htmlContent}
          </body>
        </html>
      `;

      // Convert HTML to DOCX blob using html-docx-js
      const converted = htmlDocx.asBlob(styledHtml);
      
      // Create download link
      const url = URL.createObjectURL(converted);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating Word document:', error);
      alert('There was an error generating the Word document. Please try again.');
    }
  };

  const handleBackToDashboard = () => {
    nav('/dashboard');
  };

  // Prepare CICP Letter data
  const cicpLetterData = {
    date: new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }),
    clientFirstName: patientDetails.firstName,
    clientLastName: patientDetails.lastName,
    attorneyName: patientDetails.patientAssignee?.Lawyer?.[0]
      ? `${patientDetails.patientAssignee.Lawyer[0].firstName} ${patientDetails.patientAssignee.Lawyer[0].lastName}`
      : '[ATTORNEY NAME]',
    emailAddress:
      patientDetails.patientAssignee?.Lawyer?.[0]?.email || '[EMAIL ADDRESS]',
    phoneNumber: '[PHONE NUMBER]',
  };

  if (isLoading) {
    return (
      <div className="CombinedReportPageContainer">
        <MainTopBar
          isLoggedIn={loginState.isLoggedIn}
          isSticky={true}
          showAdminButton={loginState.isAdmin}
          userName={userDetails.userName}
        />
        <div className="LoadingContainer">
          <p>Loading patient information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="CombinedReportPageContainer">
      <MainTopBar
        isLoggedIn={loginState.isLoggedIn}
        isSticky={true}
        showAdminButton={loginState.isAdmin}
        userName={userDetails.userName}
      />

      <div className="CombinedReportActionBar no-print">
        <button className="BackButton" onClick={handleBackToDashboard}>
          ← Back to Dashboard
        </button>
        <h1 className="CombinedReportTitle">
          Complete Patient Report - {patientDetails.firstName}{' '}
          {patientDetails.lastName}
        </h1>
        <div className="ActionButtons">
          <button className="DownloadPDFButton" onClick={handleDownloadPDF}>
            📥 Download PDF
          </button>
          <button className="DownloadWordButton" onClick={handleDownloadWord}>
            📄 Download Word
          </button>
          <button className="PrintButton" onClick={handlePrint}>
            🖨️ Print Report
          </button>
        </div>
      </div>

      <div className="CombinedReportContent" ref={printRef}>
        {/* Section 1: CICP Letter */}
        <div className="ReportSection CICPLetterSection">
          <CICPLetterPage asComponent={true} {...cicpLetterData} />
        </div>

        {/* Section 2: Citations */}
        <div className="ReportSection CitationSection page-break">
          <div className="SectionHeader">
            <h2>Medical Citations & Research</h2>
          </div>
          <div className="citation-content">
            <div className="citation-body">
              {symptomCitations.map((item) => (
                <div key={item.symptomId} className="citation-symptom-group">
                  <h5 className="citation-symptom-title">{toTitleCase(item.symptom)}</h5>
                  {item.citations.length === 0 && (
                    <p className="citation-no-results">No citations available.</p>
                  )}
                  {item.citations.map((c) => (
                    <div key={c.citationId} className="citation-study-item">
                      <h4 className="citation-study-title">{c.citation}</h4>
                      <p className="citation-rationale">{c.rationale}</p>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Section 3: Test Form / Questionnaire */}
        <div className="ReportSection TestFormSection page-break">
          <div className="SectionHeader">
            <h2>Patient Questionnaire & Medical History</h2>
          </div>
          <QuestionnaireReadOnly
            userId={userId}
            token={token}
            userRole={userDetails.role}
          />
        </div>
      </div>

      {/* Action Buttons (fixed at bottom) */}
      <div className="CombinedReportFooter no-print">
        <button className="DownloadPDFButtonLarge" onClick={handleDownloadPDF}>
          📥 Download as PDF
        </button>
        <button className="DownloadWordButtonLarge" onClick={handleDownloadWord}>
          📄 Download as Word
        </button>
        <button className="PrintButtonLarge" onClick={handlePrint}>
          🖨️ Print Complete Report
        </button>
      </div>
    </div>
  );
}

export default CombinedReportPage;
